import * as React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    LinearProgress,
    Menu,
    MenuItem,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../App";
import FiltersBar from "../../components/FiltersBar";
import { DownloadCsvButton } from "../../components/DownloadCsvButton";
import MeteringChart from "./components/MeteringChart";
import MeteringSelectionDrawer from "./components/MeteringSelectionDrawer";
import MeteringSummaryCard from "./components/MeteringSummaryCard";
import MeteringTable from "./components/MeteringTable";
import {
    HEADINGS,
    OPTIONS_QUERY,
    PORTFOLIO_OPTIONS_QUERY,
    PORTFOLIO_SUMS_QUERY,
    SUMS_QUERY,
    TIME_AGG,
    facilityGlobalId,
} from "./meteringGraphql";

const METRICS = {
    cost: "Cost",
    consumptionKwh: "Consumption",
    co2Kg: "CO2",
};

const headingOrder = Object.keys(HEADINGS);

// The metering page on GraphQL. Shown at /newmetering with ?staff=true until
// it replaces the legacy page at /metering.
export default function NewMetering({ facility, clientInfo, portfolioView }) {
    const { t } = useTranslation();

    const { graphqlApi } = React.useContext(ApiContext);
    const currencySymbol = clientInfo?.currency_symbol;

    const earlierDate = new Date();
    earlierDate.setDate(earlierDate.getDate() - 7);
    const [fromDate, setFromDate] = React.useState(earlierDate);
    const [toDate, setToDate] = React.useState(new Date());
    const [timeAggType, setTimeAggType] = React.useState("h");

    // What the page shows: one facility ({ facilityId, title }) or the
    // portfolio ({ portfolio: true, title }). Null while options load.
    const [scope, setScope] = React.useState(null);
    // Facilities, groups and tariffs, as selector options: [{ id, name, heading }].
    const [options, setOptions] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [draftSelected, setDraftSelected] = React.useState([]);
    // Which card's filter drawer is open: "energy", "prices" or null.
    const [openDrawer, setOpenDrawer] = React.useState(null);

    const [facilityNodes, setFacilityNodes] = React.useState([]);
    const [groupNodes, setGroupNodes] = React.useState([]);
    const [tariffNodes, setTariffNodes] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);

    const [metric, setMetric] = React.useState("cost");
    const [chartType, setChartType] = React.useState("lines");
    const [priceChartType, setPriceChartType] = React.useState("lines");
    const [metricMenuAnchor, setMetricMenuAnchor] = React.useState(null);

    // Responses can arrive out of order; only the latest request's is used.
    const latestRequest = React.useRef(0);

    const handleAPIError = (messages) => {
        window.alert([t("Couldn't retrieve data."), ...messages].join("\n"));
    };

    React.useEffect(() => {
        if (!portfolioView && !facility) return;
        setScope(null);
        graphqlApi.query(
            portfolioView ? PORTFOLIO_OPTIONS_QUERY : OPTIONS_QUERY,
            portfolioView ? {} : { facility: facilityGlobalId(facility) },
            (data) => {
                const facilities = portfolioView
                    ? data.facilities.edges.map(({ node }) => node)
                    : [data.node];
                const groups = portfolioView
                    ? data.groups.edges.map(({ node }) => node)
                    : data.node.groups.edges.map(({ node }) => node);
                // Names repeat across facilities, so the portfolio labels
                // groups and tariffs with their facility.
                const label = (item) =>
                    portfolioView ? `${item.facility.name} · ${item.name}` : item.name;
                const byHeadingThenName = (a, b) =>
                    headingOrder.indexOf(a.heading) -
                        headingOrder.indexOf(b.heading) ||
                    a.name.localeCompare(b.name);

                const facilityOptions = facilities.map((f) => ({
                    id: f.id,
                    name: f.name,
                    heading: "FACILITY",
                }));
                const groupOptions = groups.map((group) => ({
                    id: group.id,
                    name: label(group),
                    heading: group.type,
                }));
                const tariffOptions = data.metering.tariffs.edges.map(
                    ({ node: tariff }) => ({
                        id: tariff.id,
                        name: label(tariff),
                        heading: "TARIFF",
                    }),
                );
                setOptions(
                    [...facilityOptions, ...groupOptions, ...tariffOptions].sort(
                        byHeadingThenName,
                    ),
                );

                // Defaults: all facilities in the portfolio, or all location
                // groups for one facility, as on the legacy page; all tariffs.
                const locations = groupOptions
                    .filter((option) => option.heading === "LOCATION")
                    .map((option) => option.id);
                const energyDefault =
                    portfolioView || locations.length === 0
                        ? facilityOptions.map((option) => option.id)
                        : locations;
                setSelected([
                    ...energyDefault,
                    ...tariffOptions.map((option) => option.id),
                ]);
                setScope(
                    portfolioView
                        ? { portfolio: true, title: t("All facilities") }
                        : { facilityId: data.node.id, title: data.node.name },
                );
            },
            handleAPIError,
        );
    }, [facility, portfolioView]);

    React.useEffect(() => {
        if (!scope) return;
        const request = ++latestRequest.current;

        // Whole days in the browser's time zone: [start of fromDate, start of
        // the day after toDate).
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        const to = new Date(toDate);
        to.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() + 1);

        const selectedIds = (heading) =>
            options
                .filter((o) => o.heading === heading && selected.includes(o.id))
                .map((o) => o.id);
        const groups = ["LOCATION", "SYSTEM", "OTHER"].flatMap(selectedIds);
        const tariffs = selectedIds("TARIFF");
        const variables = {
            groups,
            groupCount: Math.max(groups.length, 1),
            tariffs,
            tariffCount: Math.max(tariffs.length, 1),
            from: from.toISOString(),
            to: to.toISOString(),
            timeAgg: TIME_AGG[timeAggType],
        };
        setIsLoading(true);
        graphqlApi.query(
            scope.portfolio ? PORTFOLIO_SUMS_QUERY : SUMS_QUERY,
            scope.portfolio
                ? {
                      ...variables,
                      facilityCount: Math.max(
                          options.filter((o) => o.heading === "FACILITY").length,
                          1,
                      ),
                  }
                : { ...variables, facility: scope.facilityId },
            (data) => {
                if (request !== latestRequest.current) return;
                if (scope.portfolio) {
                    setFacilityNodes(data.facilities.edges.map(({ node }) => node));
                    setGroupNodes(data.groups.edges.map(({ node }) => node));
                } else {
                    setFacilityNodes([data.node]);
                    setGroupNodes(data.node.groups.edges.map(({ node }) => node));
                }
                setTariffNodes(
                    data.metering.tariffs.edges.map(({ node }) => node),
                );
                setIsLoading(false);
            },
            (messages) => {
                if (request !== latestRequest.current) return;
                setFacilityNodes([]);
                setGroupNodes([]);
                setTariffNodes([]);
                setIsLoading(false);
                handleAPIError(messages);
            },
        );
    }, [scope, options, selected, fromDate, toDate, timeAggType]);

    // Selected energy series, in selector order.
    const series = options
        .filter(
            (option) =>
                selected.includes(option.id) && option.heading !== "TARIFF",
        )
        .map((option) => ({
            ...option,
            sums:
                [...facilityNodes, ...groupNodes].find((n) => n.id === option.id)
                    ?.calculatedHourlyEnergyDataSums ?? [],
        }));

    const priceSeries = options
        .filter(
            (option) =>
                selected.includes(option.id) && option.heading === "TARIFF",
        )
        .map((option) => ({
            ...option,
            sums:
                tariffNodes.find((tariff) => tariff.id === option.id)
                    ?.energyPriceMeans ?? [],
        }));

    const rows = series.map((s) => ({
        id: s.id,
        name: s.name,
        heading: s.heading,
        ...Object.fromEntries(
            Object.keys(METRICS).map((m) => [
                m,
                s.sums.reduce((acc, sum) => acc + sum[m], 0.0),
            ]),
        ),
    }));

    const dateString = (date) => date.toISOString().split("T")[0];
    const csvRows = rows.map((row) => ({
        name: row.name,
        type: HEADINGS[row.heading],
        consumption_kwh: row.consumptionKwh,
        cost: row.cost,
        co2_kg: row.co2Kg,
        date_from: dateString(fromDate),
        date_to: dateString(toDate),
    }));

    return (
        <Box marginBottom={5}>
            <FiltersBar
                showKeySelector={false}
                showSave={false}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={(newDate) => setFromDate(newDate)}
                onToDateChange={(newDate) => setToDate(newDate)}
                timeAggType={timeAggType}
                onTimeAggChange={(event, newValue) =>
                    newValue && setTimeAggType(newValue)
                }
                showFilterDrawerButton={false}
            />
            {isLoading && (
                <Box sx={{ width: "100%" }}>
                    <LinearProgress />
                </Box>
            )}
            <Box
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === "light"
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    p: 3,
                    minHeight: "calc(100vh - 65px)",
                }}
            >
                <Stack>
                    {/* Every facility in scope: facilities never overlap. */}
                    <MeteringSummaryCard
                        title={scope?.title ?? ""}
                        sums={facilityNodes.flatMap(
                            (node) => node.calculatedHourlyEnergyDataSums,
                        )}
                        currencySymbol={currencySymbol}
                    />
                    <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center">
                                <Button
                                    onClick={(event) =>
                                        setMetricMenuAnchor(event.currentTarget)
                                    }
                                    sx={{ textTransform: "none" }}
                                    variant="text"
                                    endIcon={<ArrowDropDownIcon />}
                                >
                                    <Typography variant="h6" color="primary">
                                        {t(METRICS[metric])}
                                    </Typography>
                                </Button>
                                <Menu
                                    open={Boolean(metricMenuAnchor)}
                                    anchorEl={metricMenuAnchor}
                                    onClose={() => setMetricMenuAnchor(null)}
                                >
                                    {Object.entries(METRICS).map(([m, label]) => (
                                        <MenuItem
                                            key={m}
                                            onClick={() => {
                                                setMetric(m);
                                                setMetricMenuAnchor(null);
                                            }}
                                        >
                                            {t(label)}
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <Box flexGrow={1} />
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<FilterAltIcon />}
                                    onClick={() => {
                                        setDraftSelected(selected);
                                        setOpenDrawer("energy");
                                    }}
                                    sx={{ mr: 2 }}
                                >
                                    {t("Filter")}
                                </Button>
                                <ToggleButtonGroup
                                    value={chartType}
                                    exclusive
                                    size="small"
                                    onChange={(event, newValue) =>
                                        newValue && setChartType(newValue)
                                    }
                                >
                                    <ToggleButton value="bars">
                                        <BarChartIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="lines">
                                        <ShowChartIcon fontSize="small" />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Box height="calc(100vh - 350px)">
                                <MeteringChart
                                    series={series}
                                    metric={metric}
                                    timeAggType={timeAggType}
                                    chartType={chartType}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center">
                                <Typography
                                    padding={1}
                                    align="left"
                                    component="h2"
                                    variant="h6"
                                    color="primary"
                                >
                                    {t("Energy price")} [{currencySymbol}]
                                </Typography>
                                <Box flexGrow={1} />
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<FilterAltIcon />}
                                    onClick={() => {
                                        setDraftSelected(selected);
                                        setOpenDrawer("prices");
                                    }}
                                    sx={{ mr: 2 }}
                                >
                                    {t("Filter")}
                                </Button>
                                <ToggleButtonGroup
                                    value={priceChartType}
                                    exclusive
                                    size="small"
                                    onChange={(event, newValue) =>
                                        newValue && setPriceChartType(newValue)
                                    }
                                >
                                    <ToggleButton value="bars">
                                        <BarChartIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="lines">
                                        <ShowChartIcon fontSize="small" />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Box height="calc(100vh - 350px)">
                                <MeteringChart
                                    series={priceSeries}
                                    metric="value"
                                    timeAggType={timeAggType}
                                    chartType={priceChartType}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="flex-end" mb={2}>
                                <DownloadCsvButton
                                    arrayOfObjectsData={csvRows}
                                    filename={`metering_data_${dateString(fromDate)}_${dateString(toDate)}.csv`}
                                    buttonText={t("Download CSV")}
                                />
                            </Stack>
                            <MeteringTable
                                rows={rows}
                                currencySymbol={currencySymbol}
                            />
                        </CardContent>
                    </Card>
                </Stack>
                <MeteringSelectionDrawer
                    open={openDrawer === "energy"}
                    onClose={() => {
                        setOpenDrawer(null);
                        setSelected(draftSelected);
                    }}
                    label={t("Groups")}
                    options={options.filter((o) => o.heading !== "TARIFF")}
                    selected={draftSelected}
                    setSelected={setDraftSelected}
                />
                <MeteringSelectionDrawer
                    open={openDrawer === "prices"}
                    onClose={() => {
                        setOpenDrawer(null);
                        setSelected(draftSelected);
                    }}
                    label={t("Tariffs")}
                    options={options.filter((o) => o.heading === "TARIFF")}
                    selected={draftSelected}
                    setSelected={setDraftSelected}
                />
            </Box>
        </Box>
    );
}
